import { GetStaticProps } from "next";
import dynamic from "next/dynamic";
import Head from "next/head";
import React from "react";
import JobListing from "@components/JobListing/JobListing";
import Breadcrumbs, { BREADCRUMBS } from "@components/Various/Seo/Breadcrumbs";
import RobotsTag from "@components/Various/Seo/RobotsTag";
import { JobListing as JobType } from "@lib/types/jobTypes";
import getJobsInSitemap from "@lib/utils/sitemap/getJobsInSitemap";
import shortenStringToLength from "@lib/utils/string/shortenStringToLength";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";

const ErrorDisplay = dynamic(() => import("@components/Various/ErrorDisplay"));

function JobPage({ job }: { job?: JobType; id: string }) {
  const { t } = useTranslation("joblisting");

  if (!job) {
    return (
      <ErrorDisplay
        title={t("notFound.title")}
        description={t("notFound.description")}
      />
    );
  }

  return (
    <>
      <Head>
        {/* String template needed as NextJS might add HTML tags during SSR otherwise */}
        <title>
          {`${shortenStringToLength(job.title, 60 - 17)} | Smarketer Jobs`}
        </title>
      </Head>

      <RobotsTag shouldIndex={job.is_active} />

      <Breadcrumbs
        breadcrumbs={[
          BREADCRUMBS.HOME,
          {
            title: job.title,
            url: `/jobs/${job.id}`,
          },
        ]}
      />
      <div className="mt-6"></div>

      <JobListing job={job} />
    </>
  );
}

export default JobPage;

const ONE_HOUR_IN_SECONDS = 60 * 60;

export const getStaticProps: GetStaticProps = async (context) => {
  const { id } = context.params!;

  const res = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/jobs/${id}?disable_rate_limit=smArk3tEr`
  );

  try {
    const data = await res.json();

    return {
      props: {
        id,
        job: data,
        ...(await serverSideTranslations(context.locale!)),
      },
      revalidate: ONE_HOUR_IN_SECONDS,
    };
  } catch (e) {
    console.log("Not found", e);

    return {
      props: {
        id: id,
        ...(await serverSideTranslations(context.locale!)),
      },
      notFound: true,
      revalidate: ONE_HOUR_IN_SECONDS,
    };
  }
};

export async function getStaticPaths() {
  const jobs = await getJobsInSitemap();

  return {
    paths: jobs.map((job) => ({
      params: { id: job.id, slug: [job.slug] },
    })),
    fallback: "blocking",
  };
}
